/**
 * ENG-8573 (slice 1 of ENG-8427): per-tool redaction for the tool-call audit log.
 *
 * THE RULE, in one line: **identity without content.** A row may say *what the
 * agent reached for*; it may never say *what was in it*. `Bash` discloses the
 * program name and not the command line; a file operation discloses a keyed
 * hash and not the path; `WebFetch` discloses the host and not the URL.
 *
 * WHY AN ALLOWLIST AND NOT A DENYLIST. The input to a tool is an arbitrary
 * JSON object authored by the model, and the set of tools grows with every
 * Claude Code release. A denylist ("strip these fields") is a promise about a
 * set nobody controls; the first tool that ships a `body` or `content` field
 * this module has never heard of becomes a content leak silently, at whatever
 * moment the fleet auto-updates. So the default is to emit NOTHING and each
 * tool kind must be added deliberately. `degradesClosed` in the tests is the
 * assertion that this stays true.
 *
 * WHY PATHS ARE HASHED WHILE ARGV[0] IS PLAIN. This corrects an asymmetry the
 * parent issue flagged as a defect rather than a design: the original shape
 * emitted `argv[0]` for Bash — so `rm` stood in for `rm -rf /srv/acme` — while
 * emitting file paths verbatim, so a Read disclosed
 * `/srv/agent/clients/acme/2026-q2-redundancy-list.xlsx` in full. Withholding
 * the command's object while disclosing the file's identity is backwards. A
 * keyed hash keeps paths correlatable ("this agent touched the same file 12
 * times") without putting customer file names in the control plane.
 *
 * WHAT THE HASH IS AND IS NOT. It is HMAC-SHA256 under a per-agent salt that
 * never leaves the host, so the control plane cannot reverse it by dictionary
 * even though the space of plausible paths is small and guessable. It is NOT a
 * cross-agent correlation key — two agents reading the same file produce
 * different hashes, deliberately. If the salt is missing this module emits
 * NOTHING rather than an unsalted digest: a bare sha256 of a file path is
 * reversible with a wordlist, so falling back to one would quietly convert the
 * protection into its absence.
 */

import { createHmac } from 'node:crypto';
import { scrubSensitive } from './memory-extractor.js';

/**
 * Bump on ANY change to the rules below — the emitted value, the field an input
 * is read from, or the kind assigned. It is stored per row precisely because
 * redaction gets tuned: without it, the oldest rows (the ones an audit surface
 * exists to serve) are the ones whose provenance is unknowable.
 */
export const REDACTION_RULE_VERSION = 'r2';

/**
 * The closed vocabulary. MUST stay in lockstep with the CHECK constraint in
 * migration 20260809000003 and with VALID_REDACTED_TARGET_KINDS in
 * packages/api/src/routes/host-runtime.ts. A kind this side invents that the
 * other two reject would fail the constraint and take the whole ingest batch
 * down with it, so a test pins the three together.
 */
export const REDACTED_TARGET_KINDS = [
  'argv0',
  'path_hash',
  'url_host',
  'mcp_tool',
  'none',
] as const;

export type RedactedTargetKind = (typeof REDACTED_TARGET_KINDS)[number];

/**
 * The exact shape a target may take, per kind. `'none'` is absent because it
 * carries no target at all.
 *
 * This is the contract that makes the column safe, and it is enforced in THREE
 * places on purpose: here (so this module cannot emit a value that contradicts
 * its own kind), in the ingest route (so a malformed host request is rejected
 * per-row), and as a CHECK constraint (so nothing can persist a violating row
 * whatever the route does). Host-side redaction alone is not enough — it makes
 * the control plane trust the writer, and this table's tenancy columns already
 * take the opposite stance for exactly that reason. (CodeRabbit.)
 *
 * A value that fails its shape cannot be content: a full command line, a file
 * path and a query string all contain characters none of these admit.
 */
export const REDACTED_TARGET_SHAPES: Record<Exclude<RedactedTargetKind, 'none'>, RegExp> = {
  argv0: /^[A-Za-z0-9._+-]{1,64}$/,
  mcp_tool: /^[A-Za-z0-9._+-]{1,64}$/,
  path_hash: /^[0-9a-f]{32}$/,
  url_host: /^[A-Za-z0-9.-]{1,253}$/,
};

export interface RedactedTarget {
  /** The scrubbed identity, or null when nothing may be disclosed. */
  target: string | null;
  /**
   * How to read `target`. `'none'` is a POSITIVE statement that this module saw
   * the call and chose to emit nothing — never conflate it with "not
   * populated", which is what a NULL kind means in the database.
   */
  kind: RedactedTargetKind;
}

export interface RedactionContext {
  /**
   * Per-agent HMAC key for path hashing. Host-local and never transmitted.
   * Empty/absent means path hashing is unavailable and file targets degrade to
   * `'none'` — see the note above on why we never fall back to an unsalted hash.
   */
  pathHashSalt: string;
  /**
   * CHARTER `logging_mode: hash-only`. See the decision recorded at the bottom
   * of this file.
   *
   * REQUIRED, not optional (ENG-8726). This shipped optional and every
   * production construction site omitted it, so a mode the charter declares and
   * lint enforces had no runtime effect at all. `false` is a legitimate and
   * common answer — it just has to be an answer, because the failure mode of
   * forgetting is a silent disclosure, not a crash. Callers that are not
   * charter-aware should resolve one with `readAgentLoggingMode` rather than
   * hardcoding `false`.
   */
  hashOnly: boolean;
}

/** Nothing is disclosed. The default, and the destination for anything unknown. */
const NONE: RedactedTarget = { target: null, kind: 'none' };

/**
 * A bare program or tool name: letters, digits, underscore and the handful of
 * punctuation marks real executables use. Deliberately strict — anything that
 * fails this is not something we are confident is content-free, so it is
 * withheld rather than guessed at. Notably NO slash (that is a path, which
 * hashes instead), no whitespace, and no shell metacharacters.
 *
 * The underscore is load-bearing for MCP: tool segments are routinely
 * `save_issue` / `aws___search_documentation`, and excluding `_` would send
 * every MCP call to 'none' — a whole disclosure class silently switched off.
 */
const SAFE_IDENTIFIER_RE = /^[A-Za-z0-9._+-]{1,64}$/;

/** `VAR=value` prefixes that precede the real program in a shell command. */
const ENV_ASSIGNMENT_RE = /^[A-Za-z_][A-Za-z0-9_]*=/;

/**
 * Keyed hash of a file path. Truncated to 32 hex chars: collision risk across
 * one agent's file set is negligible and the shorter value keeps the audit UI
 * readable. Returns null when no salt is available.
 */
export function hashPath(path: string, salt: string): string | null {
  if (!salt) return null;
  return createHmac('sha256', salt).update(path).digest('hex').slice(0, 32);
}

/**
 * Extract `argv[0]` from a shell command string.
 *
 * Handles the two shapes that actually occur in transcripts: leading
 * environment assignments (`FOO=1 git status`) and an absolute or relative
 * program path (`/usr/local/bin/agt run`). A program PATH is treated as a path
 * — hashed, not disclosed — because `./clients/acme/run-payroll.sh` is a
 * filename with exactly the sensitivity of any other, and letting it through as
 * "just argv[0]" would reopen the asymmetry this module exists to close.
 *
 * Returns null when the first token is not recognisably a bare command, which
 * covers subshells, pipelines that begin with a redirect, and anything quoted.
 */
export function parseArgv0(command: string): { name: string | null; isPath: boolean } {
  // Only the first line matters; a heredoc or multi-line script's later lines
  // are body, not program.
  const firstLine = command.split('\n', 1)[0] ?? '';
  const tokens = firstLine.trim().split(/\s+/).filter(Boolean);

  let i = 0;
  while (i < tokens.length && ENV_ASSIGNMENT_RE.test(tokens[i]!)) i++;
  const token = tokens[i];
  if (!token) return { name: null, isPath: false };

  if (token.includes('/')) return { name: token, isPath: true };
  return { name: SAFE_IDENTIFIER_RE.test(token) ? token : null, isPath: false };
}

/** Read a string field out of an untrusted tool input object. */
function stringField(input: unknown, field: string): string | null {
  if (!input || typeof input !== 'object' || Array.isArray(input)) return null;
  const v = (input as Record<string, unknown>)[field];
  return typeof v === 'string' && v.length > 0 ? v : null;
}

/**
 * `mcp__<server>__<tool>` → the tool segment. Mirrors parseMcpServer in the
 * ingest route, which derives the SERVER half server-side from the same name.
 */
function parseMcpTool(toolName: string): string | null {
  const m = /^mcp__[^_]+(?:_[^_]+)*?__(.+)$/.exec(toolName);
  const tool = m?.[1];
  return tool && SAFE_IDENTIFIER_RE.test(tool) ? tool : null;
}

/**
 * The allowlist. Every tool kind that may disclose anything is named here; the
 * function returns NONE for everything else.
 */
export function redactToolTarget(
  toolName: string,
  input: unknown,
  ctx: RedactionContext,
): RedactedTarget {
  const out = redactToolTargetInner(toolName, input, ctx);
  // Self-check: never emit a target that contradicts its own kind. Every arm
  // below already validates its output, so this should be unreachable — which
  // is the point. It converts a future arm that forgets to validate from a
  // content leak into a withholding.
  if (out.kind === 'none') return out.target === null ? out : NONE;
  return out.target !== null && REDACTED_TARGET_SHAPES[out.kind].test(out.target) ? out : NONE;
}

function redactToolTargetInner(
  toolName: string,
  input: unknown,
  ctx: RedactionContext,
): RedactedTarget {
  // hash-only agents disclose nothing beyond the tool name itself. See the
  // decision note below.
  if (ctx.hashOnly) return NONE;

  if (toolName.startsWith('mcp__')) {
    const tool = parseMcpTool(toolName);
    return tool ? { target: tool, kind: 'mcp_tool' } : NONE;
  }

  switch (toolName) {
    case 'Bash': {
      const command = stringField(input, 'command');
      if (!command) return NONE;
      const { name, isPath } = parseArgv0(command);
      if (!name) return NONE;
      if (isPath) {
        const hashed = hashPath(name, ctx.pathHashSalt);
        return hashed ? { target: hashed, kind: 'path_hash' } : NONE;
      }
      // scrubSensitive is belt-and-braces: a bare command name cannot really be
      // a credential, but this module's contract is that nothing leaves without
      // passing the secret-shape filter, and an exception invites a later one.
      const scrubbed = scrubSensitive(name);
      return SAFE_IDENTIFIER_RE.test(scrubbed) ? { target: scrubbed, kind: 'argv0' } : NONE;
    }

    // `MultiEdit`, `NotebookEdit` and `NotebookRead` are allowlisted WITHOUT
    // having been observed — 0 occurrences each across 81 real transcripts,
    // where only `Edit` appears. Normally that is exactly the speculative
    // widening this module refuses to do, so the exception is worth stating: the
    // sole disclosure on this arm is a keyed hash. If one of these names turns
    // out to belong to some unrelated future tool, the row carries an unreadable
    // digest of whatever its path field held — no content escapes either way.
    // Against that, omitting a real file tool is a SILENT audit gap, which is
    // the costlier error. (CodeRabbit.)
    case 'Read':
    case 'Write':
    case 'Edit':
    case 'MultiEdit': {
      const path = stringField(input, 'file_path');
      if (!path) return NONE;
      const hashed = hashPath(path, ctx.pathHashSalt);
      return hashed ? { target: hashed, kind: 'path_hash' } : NONE;
    }

    case 'NotebookEdit':
    case 'NotebookRead': {
      const path = stringField(input, 'notebook_path');
      if (!path) return NONE;
      const hashed = hashPath(path, ctx.pathHashSalt);
      return hashed ? { target: hashed, kind: 'path_hash' } : NONE;
    }

    case 'WebFetch': {
      const url = stringField(input, 'url');
      if (!url) return NONE;
      try {
        // URL.hostname drops userinfo, port, path, query and fragment — so
        // `https://user:token@api.example.com/v1?key=SECRET` yields exactly
        // `api.example.com`. That credential-in-URL shape is the reason this
        // goes through the parser instead of a regex.
        const host = new URL(url).hostname;
        return host ? { target: host, kind: 'url_host' } : NONE;
      } catch {
        // Not a parseable URL — withhold rather than emit a fragment of it.
        return NONE;
      }
    }

    default:
      // DEGRADE CLOSED. A tool this module has never seen discloses nothing but
      // its name (which is carried separately in tool_name). Do not add a
      // generic "emit input.path if present" fallback here — that is precisely
      // how an unreviewed future tool becomes a leak.
      return NONE;
  }
}

/**
 * sha256 of the canonicalised tool input, for `input_sha256`.
 *
 * MOVED TO CORE (ENG-8428). The API's transcript retrieval route verifies this
 * digest to prove a fetched transcript line is the one a row indexes, and the
 * API cannot import from `apps/cli`. A hand-copied second canonicaliser would
 * have to stay byte-identical to this one forever, and the drift direction is
 * the worst available: intact evidence would fail verification and be reported
 * as tampered. One implementation, shared through the compiler.
 *
 * Re-exported rather than removed so this module's public surface — and the
 * extractor plus its ENG-8573 tests — are unchanged.
 */
export { hashToolInput } from '@augmented/core/tool-calls/input-hash.js';

/**
 * DECISION — `logging_mode: hash-only` (ENG-8573 AC6, made reachable by ENG-8726).
 *
 * `packages/core/src/types/charter.ts` defines the mode and lint enforces it
 * across CHARTER/TOOLS. The question this rule had to settle: does hashing file
 * paths for every agent already satisfy `hash-only`, or do such agents need
 * something stricter?
 *
 * DECIDED: stricter. A hash-only agent emits `kind: 'none'` for EVERY tool.
 *
 * The reason is that the default rules still emit two plaintext values —
 * `argv[0]` and an MCP tool segment. Both are drawn from small, enumerable
 * vocabularies, so neither is "hashed" by any reading of the word, and an
 * operator who set `hash-only` and then found `psql` and `save_issue` in the
 * audit log would be right to call that a broken promise. Emitting nothing is
 * the only interpretation that cannot be argued with.
 *
 * What a hash-only agent still discloses: `tool_name` (and `mcp_server`, which
 * the API derives from it). Those are not redactable — they are the audit log's
 * entire subject, and a row without them indexes nothing. `hash-only` is a
 * statement about arguments, not about whether the call is recorded at all.
 *
 * ENG-8726 — WHO SETS IT. This arm was unreachable for as long as it existed:
 * `hashOnly` was optional at every seam and the one production caller never
 * supplied it, so the rule above described behaviour the fleet never exhibited.
 * It is now produced by `readAgentLoggingMode` (agent-logging-mode.ts) from the
 * provisioned CHARTER.md, and required by the compiler at both this context and
 * `ScanArgs`. An UNKNOWN mode resolves to `true` here — see that module for why
 * the fail direction is closed.
 *
 * ENG-8726 — WHY THE OTHER GRANULARITIES ARE NOT CHANGED. AC3 asks for Bash and
 * file-path granularity to be consistent and the reasoning recorded. They
 * already are, as of ENG-8573: see "WHY PATHS ARE HASHED WHILE ARGV[0] IS PLAIN"
 * at the top of this file — the original defect (argv[0] withheld, full path
 * disclosed) was corrected there, and a program given AS a path hashes rather
 * than slipping through as "just argv[0]".
 *
 * The rule those arms share is not "hash everything"; it is: disclose the
 * IDENTITY of the thing reached for at the coarsest level that still identifies
 * it, and hash that identifier when the identifier is itself customer data. A
 * program name and an MCP tool segment come from small, enumerable vocabularies
 * that name OUR software, so hashing them would destroy the row's meaning while
 * protecting nothing.
 *
 * The residual, stated rather than papered over: `url_host` is the one arm where
 * that rule is arguable, because a private hostname CAN name a customer
 * (`acme-legal.internal`). It stays plaintext deliberately — "which external
 * services did this agent reach" is a primary question the audit log exists to
 * answer, and a hashed host answers none of it. A hash-only agent discloses no
 * host at all, which is the escape hatch for anyone who disagrees. Revisiting
 * this means bumping REDACTION_RULE_VERSION, which is exactly why that field
 * is stored per row.
 */
